// Pure classifier for splitting incidents across the Threats / Alerts / Intel panels.
// Added 2026-08-25 as part of the panel co-population fix (see AUDIT_PANELS_2026-08-25.md).
//
// Design contract:
//   - Live TPD dispatch items co-populate BOTH Threats AND Alerts by default.
//   - Historical / clearly-informational items go to Intel only.
//   - The classifier NEVER mutates the incident — callers filter based on the flags.
//
// This file is intentionally dependency-free (no React, no fetch) so it can be
// unit-tested and reused server-side later.
import { Incident, ThreatLevel } from '../types';

export interface CategoryFlags {
  /** Show on the Threats panel (imminent risk to the user). */
  inThreats: boolean;
  /** Show on the Alerts panel (things to be aware of in the area). */
  inAlerts: boolean;
  /** Show on the Intel panel (historical / analytical). */
  inIntel: boolean;
  /** Human-readable label for debugging + tooltip. */
  reason: string;
}

// Age (in ms) beyond which a live incident is treated as historical.
// 6 hours by default — CAD calls older than this are almost always cleared.
const HISTORICAL_AGE_MS = 24 * 60 * 60 * 1000; // 24h so all current CAD feed calls populate live panels

// Advisory-style types that are NOT active threats but ARE area alerts:
// welfare checks, missing persons, mental-health calls, 311 requests, etc.
const ADVISORY_ONLY = /(WELFARE|MISSING|311|MENTAL HEALTH|COMMUNITY ENGAGE|SPECIAL CHECK|AGENCY ASSIST|FOLLOW UP|GENERAL ASSISTANCE|PARK PATROL)/i;

// Types that are unambiguously threats-in-progress (also stay in Alerts by default).
const ACTIVE_THREAT = /(SHOOTING|STABBING|HOMICIDE|ROBBERY|ASSAULT|WEAPON|FIREARM|PURSUIT|CONTAINMENT|BURGLARY IN PROGRESS|HOME INVASION|ARMED|SEXUAL OFFENSE|SEX CRIME|KIDNAP|HOSTAGE|BARRICADE)/i;

// Purely informational noise — these belong on the Intel/analytics surface,
// not the live safety panels. They still land on the map, but the side panel
// doesn't need to shout about them.
const INTEL_NOISE = /(311 ATL|REQUEST FOR 311|PROPERTY THEFT REPORT|COLD REPORT|VANDALISM REPORT|FRAUD REPORT|LOST PROPERTY)/i;

export function classifyIncident(
  inc: Incident,
  nowMs: number = Date.now(),
): CategoryFlags {
  const type = (inc.type || '').toUpperCase();
  const desc = (inc.description || '').toUpperCase();
  const combined = `${type} ${desc}`;
  const ageMs = nowMs - (inc.timestamp || nowMs);

  // 0. ARYA K9 hidden-game noise MUST NOT surface on any life-safety panel.
  //    A person deciding "should I go there?" cannot have game events mixed
  //    with real dispatch. RadioFeed + audio TTS + map paw markers stay
  //    untouched (allowed surfaces). Threats/Alerts/Intel stay clean.
  if (inc.id && String(inc.id).startsWith('arya-')) {
    return { inThreats: false, inAlerts: false, inIntel: false, reason: 'arya-hidden-game (life-safety filter)' };
  }

  // 1. Historical -> Intel only. Nothing older than HISTORICAL_AGE_MS lands
  //    in the live safety panels; it's noise that pushes urgent items off-screen.
  if (ageMs > HISTORICAL_AGE_MS) {
    return { inThreats: false, inAlerts: false, inIntel: true, reason: 'historical (age)' };
  }

  // 2. Active threat words -> both Threats AND Alerts (operator's default rule).
  if (ACTIVE_THREAT.test(combined) || inc.threatLevel === ThreatLevel.CRITICAL) {
    return { inThreats: true, inAlerts: true, inIntel: false, reason: 'active threat' };
  }

  // 3. Advisory-only -> Alerts only. Missing persons / welfare / 311 are
  //    situational awareness but not run away now.
  if (ADVISORY_ONLY.test(combined)) {
    return { inThreats: false, inAlerts: true, inIntel: false, reason: 'advisory' };
  }

  // 4. Pure intel noise -> Intel only. Cold reports & 311 tickets pollute the
  //    Threats panel; keep them for analytics.
  if (INTEL_NOISE.test(combined)) {
    return { inThreats: false, inAlerts: false, inIntel: true, reason: 'intel noise' };
  }

  // 5. Default for a live TPD dispatch or user report: co-populate both live
  //    panels. Safe default when the classifier is unsure — the operator asked
  //    for a fail-safe of put it in Threats + Alerts if unclear.
  if (inc.source === 'DISPATCH' || inc.source === 'RADIO' || inc.source === 'USER_REPORT' || inc.source === 'CRIME_RADAR') {
    return { inThreats: true, inAlerts: true, inIntel: false, reason: 'default live -> both' };
  }

  // 6. Unknown source: fall back to Alerts only so we never over-alarm the user.
  return { inThreats: false, inAlerts: true, inIntel: false, reason: 'unknown source' };
}

/** Convenience: filter an array of incidents down to a single panel. */
export function incidentsForPanel(
  incidents: Incident[],
  panel: 'threats' | 'alerts' | 'intel',
  nowMs: number = Date.now(),
): Incident[] {
  return incidents.filter(inc => {
    const flags = classifyIncident(inc, nowMs);
    if (panel === 'threats') return flags.inThreats;
    if (panel === 'alerts')  return flags.inAlerts;
    return flags.inIntel;
  });
}
